import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SecondaryNav from '../components/layout/SecondaryNav';
import { Users, Search, Filter, Plus, AlertCircle, LayoutGrid, List, X, BarChart3, Calendar } from 'lucide-react';
import { clientService, type Client } from '../services/clientService';
import { toast } from 'sonner';
import ClientManagementSidebar from '../components/clients/ClientManagementSidebar';
import { CreateMeetingModal, MeetingCreationData } from '../components/meeting/CreateMeetingModal';
import { httpClient } from '../lib/httpClient';
import { StatusBadge } from '../components/clients/StatusBadge'; // TASK-178
// TASK-277: Import real portfolio API service (removed mock data imports)
import { portfolioApiService } from '../services/api/portfolioApiService';
// TASK-210: Import portfolio navigation
import { useNavigationActions } from '../services/portfolio-refactor/store/portfolioStore';

// ClientCard Component for Card View
interface ClientCardComponentProps {
  client: Client;
  onViewPortfolios: () => void; // TASK-210: View portfolio details
  onViewClient: () => void; // TASK-210: View client details
  onCreateMeeting: () => void;
  aum: number | null; // TASK-209: Support null for newly created clients
  lastContactDays: number | null; // TASK-209: Support null for newly created clients
  complianceStatus: string;
}

const ClientCardComponent: React.FC<ClientCardComponentProps> = ({
  client,
  onViewPortfolios,
  onViewClient,
  onCreateMeeting,
  aum,
  lastContactDays,
  complianceStatus,
}) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('de-CH', {
      style: 'currency',
      currency: 'CHF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getComplianceBadge = () => {
    switch (complianceStatus) {
      case 'compliant':
        return <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-800">✓ Compliant</span>;
      case 'periodic-kyc-due':
        return <span className="text-xs px-2 py-1 rounded-full bg-yellow-100 text-yellow-800">⏳ KYC Review Due</span>;
      case 'incomplete-kyc':
        return <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-800">⚠️ Incomplete KYC</span>;
      case 'needs-review':
        return <span className="text-xs px-2 py-1 rounded-full bg-orange-100 text-orange-800">📋 Needs Review</span>;
      default:
        return null;
    }
  };

  const getLastContactText = () => {
    if (lastContactDays === null) return 'No contact history';
    if (lastContactDays === 0) return 'Today';
    if (lastContactDays === 1) return 'Yesterday';
    if (lastContactDays < 7) return `${lastContactDays} days ago`;
    if (lastContactDays < 30) return `${Math.floor(lastContactDays / 7)} weeks ago`;
    if (lastContactDays < 90) return `${Math.floor(lastContactDays / 30)} months ago`;
    return '90+ days ago';
  };

  // TASK-179: Always show normal card view - risk profile management is in Risk Profile tab
  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow p-5">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-kielis-navy to-blue-600 rounded-full flex items-center justify-center text-white font-semibold text-lg">
            {client.name.charAt(0).toUpperCase()}
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{client.name}</h3>
            <p className="text-sm text-gray-500">{client.type}</p>
          </div>
        </div>
        <div className="flex flex-col gap-2 items-end">
          {/* TASK-178: Status Badge */}
          {client.status && <StatusBadge status={client.status} size="sm" />}
          {/* TASK-179: Risk Profile Status Badge */}
          {client.risk_profile && (
            <span className="px-2 py-1 text-xs font-semibold rounded bg-gray-100 text-gray-800">
              {client.risk_profile.status === 'completed' ? 'Completed' :
               Object.keys(client.risk_profile).length > 0 ? 'In Progress' :
               'Not started'}
            </span>
          )}
        </div>
      </div>

      {/* AUM */}
      <div className="mb-4 pb-4 border-b border-gray-100">
        <p className="text-xs text-gray-500 mb-1">AUM</p>
        <p className="text-lg font-bold text-gray-900">
          {aum !== null ? formatCurrency(aum) : 'No data'}
        </p>
      </div>

      {/* Compliance & Last Contact */}
      <div className="space-y-2 mb-4">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">Last Contact:</span>
          <span className="font-medium text-gray-900">{getLastContactText()}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Compliance:</span>
          {getComplianceBadge()}
        </div>
      </div>

      {/* Actions - TASK-285: Removed Upload button, documents are uploaded via domain-specific uploaders */}
      <div className="grid grid-cols-3 gap-2">
        <button
          onClick={onViewPortfolios}
          className="flex flex-col items-center justify-center p-2 rounded-lg border border-gray-200 hover:border-kielis-navy hover:bg-blue-50 transition-colors"
          title="View Portfolios"
        >
          <BarChart3 className="w-4 h-4 text-kielis-navy mb-1" />
          <span className="text-xs font-medium text-gray-700">Portfolios</span>
        </button>
        <button
          onClick={onViewClient}
          className="flex flex-col items-center justify-center p-2 rounded-lg border border-gray-200 hover:border-kielis-navy hover:bg-blue-50 transition-colors"
          title="View Client Details"
        >
          <Users className="w-4 h-4 text-kielis-navy mb-1" />
          <span className="text-xs font-medium text-gray-700">Details</span>
        </button>
        <button
          onClick={onCreateMeeting}
          className="flex flex-col items-center justify-center p-2 rounded-lg border border-gray-200 hover:border-kielis-navy hover:bg-blue-50 transition-colors"
          title="Create Meeting"
        >
          <Calendar className="w-4 h-4 text-kielis-navy mb-1" />
          <span className="text-xs font-medium text-gray-700">Meeting</span>
        </button>
      </div>
    </div>
  );
};

const ClientsPage: React.FC = () => {
  const navigate = useNavigate();
  const { drillDown } = useNavigationActions(); // TASK-210: Portfolio navigation
  const [clients, setClients] = useState<Client[]>([]);
  const [clientAuM, setClientAuM] = useState<Record<string, number>>({}); // TASK-277: Real AUM data
  const [kycStatuses, setKycStatuses] = useState<Record<string, string>>({}); // KYC profile statuses by client_id
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // View mode state
  const [viewMode, setViewMode] = useState<'card' | 'table'>('card');
  const [showFilters, setShowFilters] = useState(false);

  // Filter states
  const [selectedRiskLevels, setSelectedRiskLevels] = useState<string[]>([]);
  const [selectedAUMRange, setSelectedAUMRange] = useState<string>('all');
  const [selectedLastContact, setSelectedLastContact] = useState<string>('all');
  const [selectedCompliance, setSelectedCompliance] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all'); // TASK-178

  // Sidebar state
  const [showSidebar, setShowSidebar] = useState(false);

  // Meeting modal state
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [, setSelectedClientForMeeting] = useState<Client | null>(null);
  const [isCreatingMeeting, setIsCreatingMeeting] = useState(false);

  // Fetch clients on component mount
  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    setLoading(true);
    try {
      // TASK-277: Fetch clients from real API only (no mock data)
      const response = await clientService.fetchClients(0, 100);

      // Fetch enriched data for each client to get profile data
      const enrichedClients = await Promise.all(
        response.clients.map(async (client) => {
          try {
            const enriched = await clientService.getEnrichedClient(client.id);
            return enriched;
          } catch (error) {
            console.error(`Failed to get enriched data for client ${client.id}:`, error);
            return client; // Return original if enrichment fails
          }
        })
      );

      setClients(enrichedClients);

      // TASK-277: Fetch AUM data from My Book summary
      try {
        const myBookData = await portfolioApiService.getMyBookSummary();
        const aumByClient: Record<string, number> = {};
        myBookData.clients.forEach((c) => {
          aumByClient[c.client_id] = c.total_value;
        });
        setClientAuM(aumByClient);
      } catch (aumError) {
        console.warn('Failed to fetch AUM data:', aumError);
      }

      // Fetch KYC profile statuses
      try {
        const kycRes = await httpClient.get<{ data: Array<{ client_id: string; status: string }> }>('/api/kyc/profiles/');
        const statusMap: Record<string, string> = {};
        const profiles = kycRes.data || kycRes;
        if (Array.isArray(profiles)) {
          profiles.forEach((p: any) => {
            if (p.client_id && p.status) {
              statusMap[p.client_id] = p.status;
            }
          });
        }
        setKycStatuses(statusMap);
      } catch (kycError) {
        console.warn('Failed to fetch KYC statuses:', kycError);
      }

      // TASK-877: was `toast.success(`Loaded ${n} clients from API`)`. "from API" is
      // developer language — the advisor has no other place the book could come from —
      // and a toast on every successful page load announces the unremarkable. The list
      // rendering IS the confirmation. The failure toast below is kept: that one carries
      // information the screen cannot show by itself.
    } catch (error) {
      console.error('Error fetching clients:', error);
      toast.error('Failed to load clients');
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSidebarSuccess = () => {
    // Refresh client list after successful upload or client creation
    fetchClients();
  };

  const handleCreateMeeting = async (meetingData: MeetingCreationData) => {
    setIsCreatingMeeting(true);

    try {
      const result = await httpClient.post<any>('/meeting/create', meetingData);
      console.log('Meeting created successfully:', result);

      // Close modal
      setShowMeetingModal(false);
      setSelectedClientForMeeting(null);

      toast.success('Meeting created successfully');

      // Navigate to the meeting Preparation screen (TASK-399: new 3-screen flow).
      // Go straight to /meeting/:id/prepare — routing via the old /data-gathering shim
      // (RedirectWithParams) drops the state object.
      if (result.data?.session_id) {
        navigate(`/meeting/${result.data.session_id}/prepare`, {
          state: {
            clientId: meetingData.client_id,
            clientName: meetingData.client_name
          }
        });
      }
    } catch (error: any) {
      console.error('Failed to create meeting:', error);
      toast.error(error.response?.data?.detail || 'Failed to create meeting');
    } finally {
      setIsCreatingMeeting(false);
    }
  };

  // TASK-210: Navigate to client portfolio view with proper context
  const handleViewPortfolios = async (clientId: string) => {
    try {
      // Set portfolio view context to client level
      drillDown('client', clientId);
      // Navigate to client-level portfolio view
      navigate(`/portfolio/client/${clientId}`);
    } catch (error) {
      console.error('Error navigating to portfolio view:', error);
      toast.error('Failed to navigate to portfolio view');
    }
  };

  // TASK-210: Navigate to client details page
  const handleViewClientDetails = async (clientId: string) => {
    try {
      // Navigate to client detail page
      navigate(`/clients/${clientId}`);
    } catch (error) {
      console.error('Error navigating to client details:', error);
      toast.error('Failed to navigate to client details');
    }
  };


  const getMetadataSummary = (client: Client) => {
    const metadata = client.metadata || {};
    const meetingCount = metadata.meeting_notes?.length || 0;
    const eventCount = metadata.life_events?.length || 0;
    const commCount = metadata.communications?.length || 0;

    // TASK-179: Read from separated risk_profile column (NOT metadata.profile_data)
    const riskProfile = client.risk_profile || {};

    return {
      total: meetingCount + eventCount + commCount,
      meetings: meetingCount,
      events: eventCount,
      communications: commCount,
      hasProfile: Object.keys(riskProfile).length > 0,
      profileCompleteness: calculateProfileCompleteness(riskProfile)
    };
  };

  const calculateProfileCompleteness = (riskProfile: any) => {
    // TASK-179: Direct risk_profile object (NOT profileData.risk_profile)
    if (!riskProfile || Object.keys(riskProfile).length === 0) return 0;

    // Calculate based on 10-section structure
    let score = 0;
    const total = 10;

    // Count completed sections (1-10)
    for (let i = 1; i <= 10; i++) {
      const sectionKey = `section${i}`;
      if (riskProfile[sectionKey] && Object.keys(riskProfile[sectionKey]).length > 0) {
        score++;
      }
    }

    return Math.round((score / total) * 100);
  };

  // TASK-277: Get AUM from real API data (not mock)
  const getClientAUM = (client: Client): number | null => {
    const aum = clientAuM[client.id];
    return aum !== undefined && aum > 0 ? aum : null;
  };

  const getMockLastContactDays = (client: Client): number | null => {
    // Calculate from metadata.communications
    if (!client.metadata?.communications || client.metadata.communications.length === 0) {
      return null;
    }

    // Get the most recent communication
    const sortedComms = [...client.metadata.communications].sort((a, b) =>
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );

    const lastContact = sortedComms[0];
    const lastContactDate = new Date(lastContact.date);
    const today = new Date();
    const daysDiff = Math.floor((today.getTime() - lastContactDate.getTime()) / (1000 * 60 * 60 * 24));

    return daysDiff;
  };

  const getMockComplianceStatus = (client: Client) => {
    // Check real KYC profile status from backend
    const kycStatus = kycStatuses[client.id];
    if (kycStatus === 'approved') {
      return 'compliant';
    }
    if (kycStatus === 'submitted' || kycStatus === 'in_review') {
      return 'needs-review';
    }
    if (kycStatus === 'changes_requested' || kycStatus === 'rejected') {
      return 'incomplete-kyc';
    }

    // Fallback: use risk profile completeness
    const summary = getMetadataSummary(client);
    if (summary.profileCompleteness < 100) {
      return 'incomplete-kyc';
    }

    // Check last upload date
    if (!client.last_upload_date) {
      return 'needs-review';
    }

    const daysSinceUpload = Math.floor(
      (Date.now() - new Date(client.last_upload_date).getTime()) / (1000 * 60 * 60 * 24)
    );

    // KYC review needed after 365 days
    if (daysSinceUpload > 365) {
      return 'periodic-kyc-due';
    }

    return 'compliant';
  };

  // Filter logic
  const getFilteredClients = () => {
    let filtered = [...clients];

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(c =>
        c.name.toLowerCase().includes(query) ||
        c.email.toLowerCase().includes(query)
      );
    }

    // TASK-179: Risk level filter - check determined risk profile from section6
    if (selectedRiskLevels.length > 0) {
      filtered = filtered.filter(c => {
        const determinedProfile = c.risk_profile?.section6?.determined_profile ||
                                 c.risk_profile?.section6?.risk_profile_category ||
                                 '';
        return determinedProfile && selectedRiskLevels.includes(determinedProfile.toLowerCase());
      });
    }

    // AUM filter
    if (selectedAUMRange !== 'all') {
      filtered = filtered.filter(c => {
        const aum = getClientAUM(c);
        if (aum === null) return false; // Exclude clients without AUM data
        switch (selectedAUMRange) {
          case 'under-100k': return aum < 100000;
          case '100k-500k': return aum >= 100000 && aum < 500000;
          case '500k-1m': return aum >= 500000 && aum < 1000000;
          case '1m-5m': return aum >= 1000000 && aum < 5000000;
          case 'over-5m': return aum >= 5000000;
          default: return true;
        }
      });
    }

    // Last contact filter
    if (selectedLastContact !== 'all') {
      filtered = filtered.filter(c => {
        const days = getMockLastContactDays(c);
        if (days === null) return false; // Exclude clients without contact history
        switch (selectedLastContact) {
          case 'week': return days <= 7;
          case 'month': return days <= 30;
          case '3-months': return days <= 90;
          case 'over-3-months': return days > 90;
          default: return true;
        }
      });
    }

    // Compliance filter
    if (selectedCompliance !== 'all') {
      filtered = filtered.filter(c => {
        const status = getMockComplianceStatus(c);
        return status === selectedCompliance;
      });
    }

    // TASK-178: Status filter (prospect/client)
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(c => {
        return (c.status || 'client') === selectedStatus;
      });
    }

    return filtered;
  };

  const filteredClients = getFilteredClients();

  const clearAllFilters = () => {
    setSelectedRiskLevels([]);
    setSelectedAUMRange('all');
    setSelectedLastContact('all');
    setSelectedCompliance('all');
    setSelectedStatus('all'); // TASK-178
    setSearchQuery('');
  };

  const activeFilterCount =
    selectedRiskLevels.length +
    (selectedAUMRange !== 'all' ? 1 : 0) +
    (selectedLastContact !== 'all' ? 1 : 0) +
    (selectedCompliance !== 'all' ? 1 : 0) +
    (selectedStatus !== 'all' ? 1 : 0) + // TASK-178
    (searchQuery.trim() ? 1 : 0);

  return (
    <div className="min-h-screen bg-gray-50">
      <SecondaryNav />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          {/* Header with View Toggle */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-4">
              <h2 className="text-2xl font-bold text-gray-900">Clients</h2>
              <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('card')}
                  className={`p-2 rounded transition-colors ${
                    viewMode === 'card'
                      ? 'bg-white text-kielis-navy shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  title="Card View"
                >
                  <LayoutGrid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('table')}
                  className={`p-2 rounded transition-colors ${
                    viewMode === 'table'
                      ? 'bg-white text-kielis-navy shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  title="Table View"
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
            <button
              onClick={() => setShowSidebar(true)}
              className="inline-flex items-center px-4 py-2 bg-kielis-navy text-white text-sm font-medium rounded-md hover:bg-opacity-90 transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Client
            </button>
          </div>

          {/* Search and Enhanced Filter Bar */}
          <div className="mb-6 space-y-4">
            {/* Search and Filter Toggle */}
            <div className="flex gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-kielis-gold focus:border-kielis-gold"
                />
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`inline-flex items-center px-4 py-2 border rounded-md transition-colors ${
                  showFilters || activeFilterCount > 0
                    ? 'border-kielis-navy bg-kielis-navy text-white'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters
                {activeFilterCount > 0 && (
                  <span className="ml-2 px-2 py-0.5 bg-white text-kielis-navy text-xs font-bold rounded-full">
                    {activeFilterCount}
                  </span>
                )}
              </button>
              {activeFilterCount > 0 && (
                <button
                  onClick={clearAllFilters}
                  className="inline-flex items-center px-4 py-2 text-gray-600 hover:text-gray-900"
                >
                  <X className="w-4 h-4 mr-1" />
                  Clear All
                </button>
              )}
            </div>

            {/* Filter Panel */}
            {showFilters && (
              <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  {/* Risk Level Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Risk Level</label>
                    <div className="space-y-2">
                      {['conservative', 'moderate', 'aggressive'].map((risk) => (
                        <label key={risk} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={selectedRiskLevels.includes(risk)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedRiskLevels([...selectedRiskLevels, risk]);
                              } else {
                                setSelectedRiskLevels(selectedRiskLevels.filter(r => r !== risk));
                              }
                            }}
                            className="rounded border-gray-300 text-kielis-navy focus:ring-kielis-gold"
                          />
                          <span className="ml-2 text-sm text-gray-700 capitalize">{risk}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* AUM Range Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">AUM Range</label>
                    <select
                      value={selectedAUMRange}
                      onChange={(e) => setSelectedAUMRange(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-kielis-gold"
                    >
                      <option value="all">All Ranges</option>
                      <option value="under-100k">&lt; CHF 100k</option>
                      <option value="100k-500k">CHF 100k - 500k</option>
                      <option value="500k-1m">CHF 500k - 1M</option>
                      <option value="1m-5m">CHF 1M - 5M</option>
                      <option value="over-5m">&gt; CHF 5M</option>
                    </select>
                  </div>

                  {/* Last Contact Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Last Contact</label>
                    <select
                      value={selectedLastContact}
                      onChange={(e) => setSelectedLastContact(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-kielis-gold"
                    >
                      <option value="all">Any Time</option>
                      <option value="week">Last 7 Days</option>
                      <option value="month">Last 30 Days</option>
                      <option value="3-months">Last 90 Days</option>
                      <option value="over-3-months">Over 90 Days</option>
                    </select>
                  </div>

                  {/* Compliance Status Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Compliance</label>
                    <select
                      value={selectedCompliance}
                      onChange={(e) => setSelectedCompliance(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-kielis-gold"
                    >
                      <option value="all">All Status</option>
                      <option value="compliant">Compliant</option>
                      <option value="periodic-kyc-due">KYC Review Due</option>
                      <option value="incomplete-kyc">Incomplete KYC</option>
                      <option value="needs-review">Needs Review</option>
                    </select>
                  </div>

                  {/* TASK-178: Client Status Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Client Status</label>
                    <select
                      value={selectedStatus}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-kielis-gold"
                    >
                      <option value="all">All Clients</option>
                      <option value="prospect">Prospects Only</option>
                      <option value="client">Clients Only</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Results Count */}
          {!loading && filteredClients.length > 0 && (
            <div className="mb-4 text-sm text-gray-600">
              Showing {filteredClients.length} of {clients.length} clients
              {activeFilterCount > 0 && ` (${activeFilterCount} filter${activeFilterCount > 1 ? 's' : ''} applied)`}
            </div>
          )}

          {/* Client List */}
          {loading ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-kielis-navy mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading clients...</p>
              </div>
            </div>
          ) : filteredClients.length > 0 ? (
            viewMode === 'card' ? (
              /* Card View */
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredClients.map((client) => {
                  return (
                    <ClientCardComponent
                      key={client.id}
                      client={client}
                      aum={getClientAUM(client)}
                      lastContactDays={getMockLastContactDays(client)}
                      complianceStatus={getMockComplianceStatus(client)}
                      onViewPortfolios={() => handleViewPortfolios(client.id)}
                      onViewClient={() => handleViewClientDetails(client.id)}
                      onCreateMeeting={() => {
                        setSelectedClientForMeeting(client);
                        setShowMeetingModal(true);
                      }}
                    />
                  );
                })}
              </div>
            ) : (
              /* Table View */
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Client
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      AUM
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Compliance
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredClients.map((client) => {
                    const aum = getClientAUM(client);
                    const lastContactDays = getMockLastContactDays(client);
                    const complianceStatus = getMockComplianceStatus(client);

                    const formatCurrency = (value: number) => {
                      return new Intl.NumberFormat('de-CH', {
                        style: 'currency',
                        currency: 'CHF',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      }).format(value);
                    };

                    const getLastContactText = (days: number) => {
                      if (days === 0) return 'Today';
                      if (days === 1) return 'Yesterday';
                      if (days < 7) return `${days} days ago`;
                      if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
                      if (days < 90) return `${Math.floor(days / 30)} months ago`;
                      return '90+ days ago';
                    };

                    const getComplianceBadge = (status: string) => {
                      switch (status) {
                        case 'compliant':
                          return <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-800">✓ Compliant</span>;
                        case 'periodic-kyc-due':
                          return <span className="text-xs px-2 py-1 rounded-full bg-yellow-100 text-yellow-800">⏳ KYC Review Due</span>;
                        case 'incomplete-kyc':
                          return <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-800">⚠️ Incomplete</span>;
                        case 'needs-review':
                          return <span className="text-xs px-2 py-1 rounded-full bg-orange-100 text-orange-800">📋 Review</span>;
                        default:
                          return null;
                      }
                    };

                    return (
                      <tr key={client.id} className="hover:bg-gray-50">
                        {/* Client Name */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-kielis-navy to-blue-600 rounded-full flex items-center justify-center text-white font-semibold">
                              {client.name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900">{client.name}</div>
                              <div className="text-sm text-gray-500">{client.type}</div>
                            </div>
                          </div>
                        </td>

                        {/* AUM */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-semibold text-gray-900">
                            {aum !== null ? formatCurrency(aum) : 'No data'}
                          </div>
                        </td>

                        {/* Last Contact */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {lastContactDays !== null ? getLastContactText(lastContactDays) : 'No contact history'}
                          </div>
                        </td>

                        {/* Compliance Status */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getComplianceBadge(complianceStatus)}
                        </td>

                        {/* TASK-178: Client Status */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          {client.status && <StatusBadge status={client.status} size="sm" />}
                        </td>

                        {/* Actions - TASK-285: Removed Upload button, documents are uploaded via domain-specific uploaders */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          {/* TASK-179: Always show normal actions - risk profile management is in Risk Profile tab */}
                          <div className="flex gap-1">
                            <button
                              onClick={() => handleViewPortfolios(client.id)}
                              className="p-2 text-kielis-navy hover:text-white hover:bg-kielis-navy rounded transition-colors"
                              title="View Portfolios"
                            >
                              <BarChart3 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleViewClientDetails(client.id)}
                              className="p-2 text-kielis-navy hover:text-white hover:bg-kielis-navy rounded transition-colors"
                              title="View Client Details"
                            >
                              <Users className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedClientForMeeting(client);
                                setShowMeetingModal(true);
                              }}
                              className="p-2 text-kielis-navy hover:text-white hover:bg-kielis-navy rounded transition-colors"
                              title="Create Meeting"
                            >
                              <Calendar className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            )
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-8 text-center">
                <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Clients Found</h3>
                <p className="text-gray-600 mb-4">
                  {activeFilterCount > 0 || searchQuery
                    ? 'No clients match your filters. Try adjusting your search criteria.'
                    : 'Start by adding your first client.'}
                </p>
                <button
                  onClick={() => setShowSidebar(true)}
                  className="inline-flex items-center px-4 py-2 bg-kielis-navy text-white text-sm font-medium rounded-md hover:bg-opacity-90"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add First Client
                </button>
              </div>
            </div>
          )}
          
          {/* Stats Cards - Always show based on filtered results */}
          {!loading && filteredClients.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-8">
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Users className="w-8 h-8 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-2xl font-semibold text-gray-900">{filteredClients.length}</p>
                    <p className="text-sm text-gray-600">
                      {activeFilterCount > 0 ? 'Filtered Clients' : 'Total Clients'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-green-600 font-bold">✓</span>
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-2xl font-semibold text-gray-900">
                      {filteredClients.filter(c => getMockComplianceStatus(c) === 'compliant').length}
                    </p>
                    <p className="text-sm text-gray-600">Compliant</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                      <AlertCircle className="w-5 h-5 text-yellow-600" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-2xl font-semibold text-gray-900">
                      {filteredClients.filter(c => getMockComplianceStatus(c) === 'periodic-kyc-due').length}
                    </p>
                    <p className="text-sm text-gray-600">KYC Review Due</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                      <AlertCircle className="w-5 h-5 text-red-600" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-2xl font-semibold text-gray-900">
                      {filteredClients.filter(c => {
                        const status = getMockComplianceStatus(c);
                        return status === 'incomplete-kyc' || status === 'needs-review';
                      }).length}
                    </p>
                    <p className="text-sm text-gray-600">Needs Attention</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Client Management Sidebar */}
      <ClientManagementSidebar
        isOpen={showSidebar}
        onClose={() => setShowSidebar(false)}
        onSuccess={handleSidebarSuccess}
      />

      {/* Create Meeting Modal */}
      <CreateMeetingModal
        isOpen={showMeetingModal}
        onClose={() => !isCreatingMeeting && setShowMeetingModal(false)}
        onCreateMeeting={handleCreateMeeting}
        isLoading={isCreatingMeeting}
      />
    </div>
  );
};

export default ClientsPage;